<template>
    <div>
        <div class="chart-container">
            <v-chart
                v-if="chartData.rows && chartData.rows.length > 0 && !chartLoading"
                :option="barChartOptions"
                :style="{ height: `${windowHeight * 0.4}px` }"
                class="chart"
                autoresize
            />
            <div
                v-else-if="!chartLoading"
                :style="{ height: `${windowHeight * 0.4}px` }"
                class="flex flex-center"
            >
                <WText>
                    {{ $t('visualization.customPlotPanels.attributesInfo.noData') }}
                </WText>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment'
import { dashboardChartsMixin } from '@/mixins'
import echartsColors from '@/charts/echartsColors'

export default {
    name: 'FrequencyDurationChart',
    mixins: [dashboardChartsMixin],
    props: {
        index: {
            type: [String, Number],
            required: true,
        },
        generalFiltering: {
            type: Object,
            required: true,
        },
        chartData: {
            type: Object,
            required: true,
        },
        chartLoading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['chartLoading', 'get-chart-data'],
    data() {
        return {
            duration: {
                key: "MONTH",
                label: this.$t("visualization.customPlotPanels.attributesInfo.month"),
            },
            rangeSel: {
                key: "START",
                label: this.$t("visualization.customPlotPanels.attributesInfo.start"),
            },
            ownFilters: [],
            rangeString: '',
            windowHeight: window.innerHeight - 100,
            maxDur: '',
            barChartOptions: {
                title: {
                    text: '',
                },
                tooltip: {
                    trigger: 'axis',
                    confine: true,
                    formatter: (params) => {
                        let tooltipContent = ''
                        const { name } = params[0]
                        tooltipContent += `${name}<br>`
                        params.forEach((param) => {
                            const { seriesName } = param
                            const { value } = param
                            const { color } = param
                            tooltipContent += `<div style="width: 10px; height: 10px; background-color: ${color};border-radius: 50%; display: inline-block; margin-right: 3px;">
                                </div>`
                            if (seriesName === this.$t('visualization.customPlotPanels.duration')) {
                                tooltipContent += `${seriesName}: ${moment.duration(value, this.maxDur).format(
                                    'd[d] h[h]:m[m]:s[s]',
                                    { largest: 2, trim: false },
                                )} <br>`
                            } else {
                                tooltipContent += `${seriesName}: ${value} <br>`
                            }
                        })
                        return tooltipContent
                    },
                },
                dataZoom: [
                    {
                        type: 'inside',
                        show: true,
                        start: 0,
                        end: 100,
                        handleSize: 8,
                    },
                ],
                xAxis: {
                    type: 'category',
                    data: [],
                },
                yAxis: [
                    {
                        type: 'value',
                        axisLabel: {
                            formatter: (value) => this.formatNumber(value),
                        },
                    },
                    {
                        type: 'value',
                        axisLabel: {
                            formatter: (value) => `${this.formatNumber(value)}${this.rangeString}`,
                        },
                    },
                ],
                grid: {
                    left: 45,
                    right: 45,
                    bottom: 20,
                    top: 20,
                },
                series: [
                    {
                        name: this.$t('visualization.customPlotPanels.duration'),
                        type: 'line',
                        data: [],
                        itemStyle: {
                            color: echartsColors.line.stroke,
                        },
                        yAxisIndex: 1,
                    },
                    {
                        name: this.$t('visualization.customPlotPanels.frequency'),
                        type: 'bar',
                        data: [],
                        itemStyle: {
                            color: echartsColors.bar.fill,
                        },
                        yAxisIndex: 0,
                    },
                ],
            },
        }
    },
    watch: {
        chartData: {
            handler(newVal) {
                if (newVal.rows && newVal.rows.length > 0) {
                    this.updateChart(newVal)
                }
            },
            deep: true,
        },
    },

    created() {
        this.$eventBus.on(`updateFreqDuration-${this.index}`, this.updateSelection)
    },

    mounted() {
    },

    methods: {
        updateSelection(newSel) {
            this.duration = newSel.duration
            this.rangeSel = newSel.caseType
            this.getData()
        },
        resetChart() {
            this.barChartOptions.series[1].data = []
            this.barChartOptions.series[0].data = []
            this.barChartOptions.xAxis.data = []
        },
        getData() {
            this.$emit('chartLoading', true)
            this.resetChart()
            const params = {
                generalFilters: this.generalFiltering.generalFilters || [],
                dashboardFilters: this.generalFiltering.dashboardFilters || [],
                sortBy: 'FREQUENCY',
                ownFilters: [],
                filterSets: this.generalFiltering.filterSets || [],
                operator: this.generalFiltering.operator,
                duration: this.duration,
                caseType: this.rangeSel,
                limit: 8,
                start: 0,
            }
            this.$emit('get-chart-data', params)
            this.$emit('chartLoading', false)
        },
        updateChart(returnData) {
            this.maxDur = this.rangeDuration(Math.max.apply(null, [returnData.rows.map((item) => (item.duration))][0]))
            const durationArr = [returnData.rows.map((item) => this.formatSeconds(item.duration, this.maxDur))][0]
            const frequencyArr = [returnData.rows.map((item) => item.frequency)][0]
            const commonAttributes = [returnData.rows.map((item) => item.timeString)][0]
            this.barChartOptions.series[1].data = frequencyArr
            this.barChartOptions.series[0].data = durationArr
            this.barChartOptions.xAxis.data = commonAttributes
        },
    },
}
</script>
<style lang="scss" scoped>
  .chart-container {
    margin: 0 auto;
    .chart {
        width: 100%;
        margin-bottom:5px;
        & + .chart {
          margin-left: 30px;
          @media screen and (max-width: $lg) {
                margin-left: 0px;
            }
        }
    }
}
</style>
